/**
 * Copy activity/factor evidence onto sector observations.
 * Does not compute emissions.
 */

import { seriesStatusAt, seriesValueAt, type GhgInventoryEvidence } from '../../services/ghgInventoryEvidence';
import type { ReportSectorObservation } from './types';
import { mapDocumentedSourceStatus, reportValue } from './valueHelpers';

function attach(
  obs: ReportSectorObservation,
  evidence: GhgInventoryEvidence,
  year: number,
  seriesKey: keyof Pick<
    GhgInventoryEvidence,
    'ldvVmt' | 'hdvVmt' | 'elecResKwh' | 'elecComKwh' | 'ngResTherms' | 'ngComTherms' | 'wasteTons'
  >,
  factor?: { value: number | null; unit: string; note?: string }
): ReportSectorObservation {
  const series = evidence[seriesKey];
  const activity = seriesValueAt(series, year);
  const rawStatus = seriesStatusAt(series, year);
  const status = activity == null ? 'missing' : mapDocumentedSourceStatus(rawStatus || 'modeled');
  return {
    ...obs,
    activity: reportValue(activity, series.unit, status, {
      sourceTable: 'fetchGHGInventoryData.inventoryEvidence',
      sourceColumn: String(seriesKey),
      sourceYear: year,
      calculationRule:
        seriesKey === 'hdvVmt'
          ? 'Copied from emfac2025_fleet.total_vmt on the same GHG calculation run. Treat as the emissions identity only when the HDV activity role is calculation_input.'
          : 'Copied from the same GHG calculation run; not reverse-engineered from emissions',
    }),
    activityUnit: series.unit,
    emissionFactor:
      factor && factor.value != null
        ? reportValue(factor.value, factor.unit, 'modeled', {
            sourceTable: 'fetchGHGInventoryData.inventoryEvidence',
            sourceYear: year,
            calculationRule: factor.note ?? 'Factor used by the shared GHG calculation',
          })
        : obs.emissionFactor,
    emissionFactorUnit: factor?.unit ?? obs.emissionFactorUnit,
  };
}

export function attachEvidenceToSectors(
  sectors: ReportSectorObservation[],
  evidence: GhgInventoryEvidence | undefined,
  year: number
): ReportSectorObservation[] {
  if (!evidence) return sectors;
  const wasteFactor = seriesValueAt(evidence.wasteEmissionFactorMtPerTon, year);
  return sectors.map((s) => {
    switch (s.sectorCode) {
      case 'ldv':
        return attach(s, evidence, year, 'ldvVmt');
      case 'hdv':
        return attach(s, evidence, year, 'hdvVmt');
      case 'elec_res':
        return attach(s, evidence, year, 'elecResKwh', {
          value: seriesValueAt(evidence.electricityCarbonIntensityGPerKwh, year),
          unit: evidence.electricityCarbonIntensityGPerKwh.unit,
          note: 'Grid carbon intensity used by the GHG calculation (g CO2e/kWh). Not converted in the report.',
        });
      case 'elec_com':
        return attach(s, evidence, year, 'elecComKwh', {
          value: seriesValueAt(evidence.electricityCarbonIntensityGPerKwh, year),
          unit: evidence.electricityCarbonIntensityGPerKwh.unit,
          note: 'Grid carbon intensity used by the GHG calculation (g CO2e/kWh). Not converted in the report.',
        });
      case 'ng_res':
        return attach(s, evidence, year, 'ngResTherms', {
          value: evidence.ngEmissionFactorMtPerTherm,
          unit: 'MT CO2e/therm',
          note: evidence.ngEmissionFactorNote,
        });
      case 'ng_com':
        return attach(s, evidence, year, 'ngComTherms', {
          value: evidence.ngEmissionFactorMtPerTherm,
          unit: 'MT CO2e/therm',
          note: evidence.ngEmissionFactorNote,
        });
      case 'waste':
        return attach(s, evidence, year, 'wasteTons', {
          value: wasteFactor,
          unit: 'MT CO2e/ton',
          note: 'County landfill factor used by the GHG calculation',
        });
      default:
        return s;
    }
  });
}
